import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  quotationsApi,
  type QuotationStatus,
  type QuotationSummary
} from '@/api/modules/quotations';
import { PaginatedList } from '@/components/data/PaginatedList';
import { paths } from '@/router/paths';

const STATUS_COLORS: Record<QuotationStatus, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  DRAFT: 'default',
  SENT: 'primary',
  ACCEPTED: 'success',
  REJECTED: 'error',
  EXPIRED: 'warning'
};

export function QuotationListPage() {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['quotations', 'summaries', page],
    queryFn: () => quotationsApi.summaries(page, 20)
  });

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h1" gutterBottom>
            Quotations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customer quotes. Tap a row for detail; use actions to advance state.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(paths.quotationCreate)}
        >
          New quotation
        </Button>
      </Stack>

      <PaginatedList<QuotationSummary>
        page={query.data}
        isLoading={query.isLoading}
        error={query.error}
        onPageChange={setPage}
        renderItem={(q) => (
          <Card variant="outlined">
            <CardActionArea
              onClick={() =>
                navigate(paths.quotationDetail.replace(':id', q.id))
              }
            >
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h3">{q.code}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Customer: {q.customerCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {q.currency} {q.totalAmount}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={q.status}
                    color={STATUS_COLORS[q.status]}
                  />
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        )}
        emptyMessage="No quotations yet. Click New quotation to create one."
      />
    </Box>
  );
}
